"use client"

import React from "react"
import { useRouter } from "next/router"

import { useOrganization } from "@clerk/nextjs"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@coval/ui"

import { useHeaderActions } from "@/contexts/HeaderActionsContext"
import {
  formatBreadcrumb,
  isOmittedBreadcrumbSegment,
} from "@/utils/breadcrumbLabels"
import { parseWorkspaceRoute } from "@/utils/workspaceRoutes"

import { HeaderActionControls } from "./HeaderActionControls"

interface DefaultPageHeaderProps {
  getHref: (baseHref: string) => string
}

interface BreadcrumbEntry {
  key: string
  node: React.ReactNode
}

export const DefaultPageHeader = ({ getHref }: DefaultPageHeaderProps) => {
  const router = useRouter()
  const { organization } = useOrganization()
  const { headerAction } = useHeaderActions()

  const getBreadcrumbs = () => {
    const parsed = parseWorkspaceRoute(router.asPath)
    if (parsed) {
      return parsed.relativePath.split("/").filter(Boolean)
    }
    const pathname = router.asPath.split("?")[0]
    const segments = pathname
      .split("/")
      .filter((segment) => segment && segment !== organization?.slug)
    return segments.map((segment) => segment.split("#")[0])
  }

  const buildPageBreadcrumbs = (): BreadcrumbEntry[] => {
    const segments = getBreadcrumbs()
    const items: BreadcrumbEntry[] = []

    segments.forEach((segment, index) => {
      if (isOmittedBreadcrumbSegment(segment, index, segments.length)) {
        return
      }

      const arr = segments
      const isLast = index === arr.length - 1

      const path = `/${arr.slice(0, index + 1).join("/")}`
      items.push({
        key: `${segment}-${index}`,
        node: isLast ? (
          <BreadcrumbPage>{formatBreadcrumb(segment, index)}</BreadcrumbPage>
        ) : (
          <BreadcrumbLink href={getHref(path)}>
            {formatBreadcrumb(segment, index)}
          </BreadcrumbLink>
        ),
      })
    })

    return items
  }

  const breadcrumbEntries = buildPageBreadcrumbs()

  return (
    <div className="flex flex-1 min-w-0 items-center justify-between">
      <div className="flex justify-start gap-2 items-center flex-1 min-w-0">
        <Breadcrumb>
          <BreadcrumbList>
            {breadcrumbEntries.map((entry, i) => (
              <React.Fragment key={entry.key}>
                {i > 0 && <BreadcrumbSeparator />}
                <BreadcrumbItem>{entry.node}</BreadcrumbItem>
              </React.Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <HeaderActionControls headerAction={headerAction} />
      </div>
    </div>
  )
}
